/**
 * 플랜 공유. 피그마 공유 창처럼 초대 칸, 접근할 수 있는 사람, 사람 목록으로 들어간다.
 */
import { Menu, MenuItemRadio, MenuList, MenuPopover, MenuTrigger, makeStyles, mergeClasses, tokens } from "@fluentui/react-components";
import { ChevronDown16Regular, ChevronLeft16Regular, ChevronRight16Regular, Link16Regular, People16Regular } from "@fluentui/react-icons";
import { useState } from "react";
import { AppDialog } from "@/components/app-dialog";
import { FuiButton, FuiInput } from "@/components/fui-control";
import { PersonAvatar } from "./PersonAvatar";
import {
  addMockShare,
  getMockPlanAccess,
  inviteResultSummary,
  mockShareCopyText,
  setMockMemberRole,
  splitInviteEmails,
  submitMockInvites,
  useMockPlanAccess,
  type MockMember,
} from "./mock-plan-access";
import { useCan } from "./use-plan-access";

const ROLES = ["관리자", "편집자", "뷰어"] as const;

const useStyles = makeStyles({
  stack: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    minWidth: 0,
  },
  invite: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  field: {
    flex: 1,
    minWidth: 0,
  },
  note: {
    margin: 0,
    fontSize: "12px",
    lineHeight: "16px",
    color: tokens.colorNeutralForeground3,
  },
  section: {
    margin: 0,
    fontSize: "13px",
    lineHeight: "18px",
    color: tokens.colorNeutralForeground3,
  },
  people: {
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
  },
  row: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    width: "100%",
    minHeight: "44px",
    padding: "6px 2px",
    border: "none",
    background: "transparent",
    color: tokens.colorNeutralForeground1,
    font: "inherit",
    fontSize: "14px",
    lineHeight: "20px",
    textAlign: "left",
    cursor: "pointer",
    borderRadius: tokens.borderRadiusMedium,
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
    ":focus-visible": {
      outline: `2px solid ${tokens.colorStrokeFocus2}`,
      outlineOffset: "1px",
    },
  },
  rowStatic: {
    cursor: "default",
    ":hover": {
      backgroundColor: "transparent",
    },
  },
  who: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    minWidth: 0,
    flex: 1,
  },
  name: {
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  access: {
    display: "inline-flex",
    alignItems: "center",
    gap: "2px",
    flexShrink: 0,
    color: tokens.colorNeutralForeground2,
    fontSize: "13px",
    lineHeight: "18px",
  },
  roleBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: "2px",
    flexShrink: 0,
    height: "28px",
    padding: "0 6px",
    border: "none",
    borderRadius: tokens.borderRadiusMedium,
    background: "transparent",
    color: tokens.colorNeutralForeground2,
    font: "inherit",
    fontSize: "13px",
    lineHeight: "18px",
    cursor: "pointer",
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground1Hover,
      color: tokens.colorNeutralForeground1,
    },
    ":focus-visible": {
      outline: `2px solid ${tokens.colorStrokeFocus2}`,
      outlineOffset: "1px",
    },
  },
  list: {
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
    maxHeight: "360px",
    overflow: "auto",
  },
  backTitle: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    minWidth: 0,
  },
  back: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "28px",
    height: "28px",
    padding: 0,
    border: "none",
    borderRadius: tokens.borderRadiusMedium,
    background: "transparent",
    color: tokens.colorNeutralForeground2,
    cursor: "pointer",
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground1Hover,
      color: tokens.colorNeutralForeground1,
    },
    ":focus-visible": {
      outline: `2px solid ${tokens.colorStrokeFocus2}`,
      outlineOffset: "1px",
    },
  },
  org: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "32px",
    height: "32px",
    borderRadius: tokens.borderRadiusCircular,
    backgroundColor: tokens.colorNeutralBackground3,
    color: tokens.colorNeutralForeground2,
    flexShrink: 0,
  },
});

function accessLabel(role: string): string {
  if (role === "뷰어") return "볼 수 있음";
  if (role === "소유자") return "소유자";
  return "편집 가능";
}

export function PlanShareDialog({
  open,
  onClose,
  planName,
  workspaceName = "워크스페이스",
}: {
  open: boolean;
  onClose: () => void;
  planName: string;
  workspaceName?: string;
  onOpenSettings?: () => void;
}) {
  const styles = useStyles();
  const canManage = useCan("plan.member.manage");
  const canShare = useCan("plan.share.create");
  const access = useMockPlanAccess();
  const [view, setView] = useState<"home" | "people">("home");
  const [emails, setEmails] = useState("");
  const [notice, setNotice] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const people = access.members.filter((person) => person.status === "활성");
  const first = people[0];
  const rest = Math.max(0, people.length - 1);

  const close = () => {
    setView("home");
    setNotice(null);
    onClose();
  };

  const invite = () => {
    const list = splitInviteEmails(emails);
    if (list.length === 0) {
      setNotice("이메일 주소를 입력하세요.");
      return;
    }
    const result = submitMockInvites(list, "편집자");
    setNotice(inviteResultSummary(result.invited.length, result.failed));
    if (result.invited.length > 0) setEmails("");
  };

  const copyLink = async () => {
    let share = getMockPlanAccess().shares[0];
    if (!share && canShare) {
      addMockShare();
      share = getMockPlanAccess().shares.at(-1);
    }
    if (!share) {
      setNotice("복사할 링크가 없습니다.");
      return;
    }
    try {
      await navigator.clipboard.writeText(mockShareCopyText(share.id));
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      setNotice("링크를 복사하지 못했습니다.");
    }
  };

  const title =
    view === "people" ? (
      <span className={styles.backTitle}>
        <button type="button" className={styles.back} aria-label="뒤로" onClick={() => setView("home")}>
          <ChevronLeft16Regular />
        </button>
        이 플랜의 공동작업자
      </span>
    ) : (
      `${planName} 공유`
    );

  return (
    <AppDialog
      open={open}
      onClose={close}
      width="md"
      title={title}
      headerAction={
        view === "home" ? (
          <FuiButton appearance="transparent" icon={<Link16Regular />} onClick={() => void copyLink()}>
            {copied ? "복사함" : "링크 복사"}
          </FuiButton>
        ) : undefined
      }
    >
      {view === "home" ? (
        <div className={styles.stack}>
          <div className={styles.invite}>
            <FuiInput
              className={styles.field}
              density="dialog"
              value={emails}
              placeholder="이메일, 쉼표로 구분"
              aria-label="초대할 이메일"
              disabled={!canManage}
              onChange={(_, data) => setEmails(data.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  if (canManage) invite();
                }
              }}
            />
            <FuiButton appearance="primary" disabled={!canManage} onClick={invite}>
              초대
            </FuiButton>
          </div>
          {notice ? <p className={styles.note}>{notice}</p> : null}
          <p className={styles.section}>액세스 권한이 있는 사람</p>
          <div className={styles.people}>
            <div className={mergeClasses(styles.row, styles.rowStatic)}>
              <span className={styles.who}>
                <span className={styles.org} aria-hidden>
                  <People16Regular />
                </span>
                <span className={styles.name}>{workspaceName}에 있는 모든 사람</span>
              </span>
              <span className={styles.access}>편집 가능</span>
            </div>
            {first ? (
              <button type="button" className={styles.row} onClick={() => setView("people")}>
                <span className={styles.who}>
                  <PersonAvatar name={first.name} size={32} tip={false} />
                  <span className={styles.name}>{rest > 0 ? `${first.name} 외 ${rest}개` : first.name}</span>
                </span>
                <span className={styles.access}>
                  액세스 가능
                  <ChevronRight16Regular />
                </span>
              </button>
            ) : (
              <p className={styles.note}>아직 이 플랜에 들어온 사람이 없습니다.</p>
            )}
          </div>
        </div>
      ) : (
        <div className={styles.list}>
          {people.map((person) => (
            <PersonRow key={person.id} person={person} canManage={canManage} />
          ))}
        </div>
      )}
    </AppDialog>
  );
}

function PersonRow({ person, canManage }: { person: MockMember; canManage: boolean }) {
  const styles = useStyles();
  const locked = person.role === "소유자" || !canManage;
  return (
    <div className={mergeClasses(styles.row, styles.rowStatic)}>
      <span className={styles.who}>
        <PersonAvatar name={person.name} size={32} tip={false} />
        <span className={styles.name}>{person.name}</span>
      </span>
      {locked ? (
        <span className={styles.access}>{accessLabel(person.role)}</span>
      ) : (
        <Menu
          positioning="below-end"
          checkedValues={{ role: [person.role] }}
          onCheckedValueChange={(_, data) => {
            const next = data.checkedItems[0];
            if (next) setMockMemberRole(person.id, next);
          }}
        >
          <MenuTrigger disableButtonEnhancement>
            <button type="button" className={styles.roleBtn} aria-label={`${person.name} 권한`}>
              {accessLabel(person.role)}
              <ChevronDown16Regular />
            </button>
          </MenuTrigger>
          <MenuPopover>
            <MenuList>
              {ROLES.map((role) => (
                <MenuItemRadio key={role} name="role" value={role}>
                  {role}
                </MenuItemRadio>
              ))}
            </MenuList>
          </MenuPopover>
        </Menu>
      )}
    </div>
  );
}
